import { useEffect, useState } from "react";
import api from "@/lib/api";
import { TID } from "@/constants/testIds";
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid, BarChart, Bar } from "recharts";

const CATEGORY_LABEL = {
  onboarding: "Onboarding", product: "Product", learning: "Learning", assessment: "Assessment",
  support: "Support", complaint: "Complaint", recommendation: "Recommendation",
  profile_update: "Profile Update", consent: "Consent", communication: "Communication",
};

export default function ReportsPage() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/reports/summary").then(r => setData(r.data)); }, []);

  return (
    <div data-testid={TID.reportsPage} className="space-y-6 animate-fade-in-up">
      <header className="flex items-end justify-between border-b border-border pb-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-altara-teal font-semibold">Reports</div>
          <h1 className="font-display text-3xl font-semibold tracking-tight text-altara-ink mt-1">Executive reporting</h1>
          <p className="text-sm text-muted-foreground mt-1">Board-ready views. Every metric traces back to evidence.</p>
        </div>
        <div className="font-mono text-[10px] text-muted-foreground">Preview · non-interactive</div>
      </header>

      <div className="grid md:grid-cols-4 gap-4">
        <ReportKPI label="Consumers" value={data?.total_consumers} />
        <ReportKPI label="Open recs" value={data?.open_recommendations} />
        <ReportKPI label="Evidence" value={data?.evidence_records} />
        <ReportKPI label="Avg trust" value={`${data?.trust_score_avg ?? "…"} / 100`} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 border border-slate-200 rounded-sm p-4 bg-white">
          <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-2">Trust trend · 12 weeks</div>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data?.trust_trend || []}>
                <CartesianGrid stroke="#e2e8f0" strokeDasharray="2 4" />
                <XAxis dataKey="week" tick={{ fill: "#64748b", fontSize: 10 }} axisLine={{ stroke: "#e2e8f0" }} tickLine={false} />
                <YAxis domain={[0, 100]} tick={{ fill: "#64748b", fontSize: 10 }} axisLine={{ stroke: "#e2e8f0" }} tickLine={false} width={30} />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 2 }} />
                <Line type="monotone" dataKey="score" stroke="#0D9488" strokeWidth={2} dot={{ r: 2, fill: "#0A2540" }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="border border-slate-200 rounded-sm p-4 bg-white">
          <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-2">Timeline categories</div>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={(data?.category_breakdown || []).map(c => ({ name: CATEGORY_LABEL[c.category] || c.category, count: c.count }))} layout="vertical">
                <CartesianGrid stroke="#e2e8f0" strokeDasharray="2 4" horizontal={false} />
                <XAxis type="number" tick={{ fill: "#64748b", fontSize: 10 }} axisLine={{ stroke: "#e2e8f0" }} tickLine={false} />
                <YAxis dataKey="name" type="category" tick={{ fill: "#334155", fontSize: 11 }} axisLine={{ stroke: "#e2e8f0" }} tickLine={false} width={110} />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 2 }} />
                <Bar dataKey="count" fill="#0A2540" radius={[0, 2, 2, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
}

function ReportKPI({ label, value }) {
  return (
    <div className="border border-slate-200 rounded-sm p-4 bg-white">
      <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">{label}</div>
      <div className="font-display text-2xl font-semibold text-altara-ink mt-1 tabular-nums">{value ?? "…"}</div>
    </div>
  );
}
